import { Link } from 'react-router-dom'
import {
  Gauge, Wind, Droplet, Calculator, Thermometer,
  Activity, Beaker, FlaskConical, Layers, AlertTriangle,
  ArrowRightLeft, Clock
} from 'lucide-react'
import AdSense from './AdSense'
import RecentCalculations from './RecentCalculations'
import './Home.css'
import './AdSense.css'

const iconMap: Record<string, any> = {
  'gas-choke': Gauge,
  'gas-velocity': Wind,
  'gor2': Droplet,
  'z-factor': Activity,
  'combined-meter': Calculator,
  'katz-shrinkage': Layers,
  'shrinkage': Layers,
  'specific-gravity': Beaker,
  'volume-correction': FlaskConical,
  'kill-fluid': AlertTriangle,
  'pressure': Gauge,
  'temperature': Thermometer,
  'volume': Droplet,
  'density': Beaker,
}

const calculators = [
  {
    title: 'Gas Choke Rate',
    path: '/calculators/gas-choke',
    description: 'Calculate gas choke rates for optimal production control',
    category: 'gas',
    icon: 'gas-choke'
  },
  {
    title: 'Gas Velocity',
    path: '/calculators/gas-velocity',
    description: 'Calculate gas velocity in pipelines and wells',
    category: 'gas',
    icon: 'gas-velocity'
  },
  {
    title: 'GOR2 Computation',
    path: '/calculators/gor2',
    description: 'Calculate Gas Oil Ratio for reservoir analysis',
    category: 'gas',
    icon: 'gor2'
  },
  {
    title: 'Gas Compressibility (Z-Factor)',
    path: '/calculators/z-factor',
    description: 'Calculate gas compressibility factor using Hall-Yarborough correlation',
    category: 'gas',
    icon: 'z-factor'
  },
  {
    title: 'Combined Meter Factor',
    path: '/calculators/combined-meter',
    description: 'Calculate combined meter factors for flow measurement',
    category: 'oil',
    icon: 'combined-meter'
  },
  {
    title: 'Katz Shrinkage',
    path: '/calculators/katz-shrinkage',
    description: 'Calculate Katz shrinkage factor for natural gas liquids',
    category: 'oil',
    icon: 'katz-shrinkage'
  },
  {
    title: 'Shrinkage',
    path: '/calculators/shrinkage',
    description: 'Calculate oil and gas shrinkage factors',
    category: 'oil',
    icon: 'shrinkage'
  },
  {
    title: 'Specific Gravity',
    path: '/calculators/specific-gravity',
    description: 'Calculate specific gravity of oil and gas fluids',
    category: 'oil',
    icon: 'specific-gravity'
  },
  {
    title: 'Volume Correction Factor',
    path: '/calculators/volume-correction',
    description: 'Calculate volume correction for temperature variations',
    category: 'oil',
    icon: 'volume-correction'
  },
  {
    title: 'Kill Fluid & Safety Margin',
    path: '/calculators/kill-fluid',
    description: 'Calculate kill fluid density for safe well control operations',
    category: 'oil',
    icon: 'kill-fluid'
  },
  {
    title: 'Pressure Converter',
    path: '/converters/pressure',
    description: 'Convert between pressure units: PSI, bar, kPa, and more',
    category: 'conversion',
    icon: 'pressure'
  },
  {
    title: 'Temperature Converter',
    path: '/converters/temperature',
    description: 'Convert between Celsius, Fahrenheit, and Kelvin',
    category: 'conversion',
    icon: 'temperature'
  },
  {
    title: 'Volume Converter',
    path: '/converters/volume',
    description: 'Convert between barrels, gallons, liters, and more',
    category: 'conversion',
    icon: 'volume'
  },
  {
    title: 'Density Converter',
    path: '/converters/density',
    description: 'Convert between kg/m³, g/cm³, lb/ft³, ppg, and calculate API gravity',
    category: 'conversion',
    icon: 'density'
  }
]

const comingSoonCalculators = [
  {
    title: 'Reservoir Pressure Drop',
    description: 'Calculate pressure drop in reservoir systems',
    category: 'coming-soon'
  },
  {
    title: 'Pipeline Flow Rate',
    description: 'Determine optimal flow rates for pipeline operations',
    category: 'coming-soon'
  },
  {
    title: 'Well Productivity Index',
    description: 'Calculate well productivity and efficiency metrics',
    category: 'coming-soon'
  },
  {
    title: 'Oil Formation Volume Factor',
    description: 'Calculate formation volume factor for oil reservoirs',
    category: 'coming-soon'
  },
  {
    title: 'Water Cut Analysis',
    description: 'Analyze and calculate water cut in production',
    category: 'coming-soon'
  }
]

const categories = [
  { id: 'gas', title: 'Gas Calculators', icon: Wind, color: 'petroleum-blue' },
  { id: 'oil', title: 'Oil Calculators', icon: Droplet, color: 'drill-green' },
  { id: 'conversion', title: 'Unit Converters', icon: ArrowRightLeft, color: 'safety-orange' },
]

export default function Home() {
  return (
    <div className="min-h-screen py-8 px-6 w-full" style={{ maxWidth: '1600px', margin: '0 auto' }}>
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
          Professional Oil & Gas Calculators
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          Free engineering tools for petroleum professionals
        </p>
      </div>

      <div className="mb-8">
        <RecentCalculations />
      </div>

      <div className="ad-container horizontal mb-8">
        <AdSense adSlot="1234567890" adFormat="auto" />
      </div>

      {categories.map((category) => {
        const CategoryIcon = category.icon
        const categoryCalcs = calculators.filter(c => c.category === category.id)

        return (
          <section key={category.id} className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <CategoryIcon className={`w-8 h-8 text-${category.color}`} />
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                {category.title}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {categoryCalcs.map(calc => {
                const Icon = iconMap[calc.icon]
                return (
                  <Link
                    to={calc.path}
                    key={calc.path}
                    className="calc-card hover:shadow-lg hover:scale-105 transform transition-all duration-200 group"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg bg-${category.color}/10 group-hover:bg-${category.color}/20 transition-colors`}>
                        <Icon className={`w-6 h-6 text-${category.color}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white group-hover:text-safety-orange transition-colors">
                          {calc.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {calc.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          </section>
        )
      })}

      <div className="ad-container horizontal mb-8">
        <AdSense adSlot="1234567891" adFormat="auto" />
      </div>

      <section className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <Clock className="w-8 h-8 text-gray-400" />
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            Coming Soon
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {comingSoonCalculators.map((calc, index) => (
            <div key={index} className="calc-card relative opacity-75 cursor-not-allowed">
              <div className="absolute top-4 right-4 bg-warning-yellow text-gray-900 text-xs font-semibold px-3 py-1 rounded-full">
                Coming Soon
              </div>
              <div className="pr-24">
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                  {calc.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {calc.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
